import { useState, useEffect } from "react";
import { apiClient } from "../services/api";
import { useAuth } from "./useAuth";


// Helper function to get display name from first and last name
export const getDisplayName = (firstName?: string, lastName?: string, fallbackName?: string): string => {
  const first = firstName || '';
  const last = lastName || '';
  
  if (!first && !last) {
    return fallbackName || 'Unknown Client';
  }
  if (!last) return first;
  if (!first) return last;
  return `${first} ${last}`;
};


export interface Case {
  id: string;
  caseId: string;
  clientName: string; // Keep for backward compatibility
  clientFirstName?: string; // New separate field
  clientLastName?: string;  // New separate field
  clientEmail: string;
  caseType: string;
  status: string;
  priority: string;
  lastUpdated: string;
  createdAt: string;
  title?: string;
  description?: string;
  clientId?: string;
  country?: string;
}

export const useCasesApi = () => {
  const [cases, setCases] = useState<Case[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchCases = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!user) {
        setCases([]);
        return;
      }

      const data = await apiClient.getCases();
      setCases(data);
    } catch (error) {
      console.error("Error fetching cases:", error);
      setError(
        error instanceof Error ? error.message : "Failed to fetch cases"
      );
      setCases([]);
    } finally {
      // Add a small artificial delay (100ms) to make the spinner more noticeable
      setTimeout(() => {
        setLoading(false);
      }, 100);
    }
  };

  const createCase = async (caseData: Partial<Case>) => {
    try {
      setError(null);

      if (!user) {
        throw new Error("User not authenticated");
      }

      const newCase = await apiClient.createCase(caseData);
      setCases((prev) => [newCase, ...prev]);
      return newCase;
    } catch (error) {
      console.error("Error creating case:", error);
      setError(
        error instanceof Error ? error.message : "Failed to create case"
      );
      throw error;
    }
  };

  const updateCase = async (id: string, updates: Partial<Case>) => {
    try {
      setError(null);

      const updatedCase = await apiClient.updateCase(id, updates);
      setCases((prev) =>
        prev.map((case_) => (case_.id === id ? updatedCase : case_))
      );
      return updatedCase;
    } catch (error) {
      console.error("Error updating case:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update case"
      );
      throw error;
    }
  };

  const deleteCase = async (id: string) => {
    try {
      setError(null);

      await apiClient.deleteCase(id);
      setCases((prev) => prev.filter((case_) => case_.id !== id));
    } catch (error) {
      console.error("Error deleting case:", error);
      setError(
        error instanceof Error ? error.message : "Failed to delete case"
      );
      throw error;
    }
  };

  const forceRefresh = async () => {
    await fetchCases();
  };

  useEffect(() => {
    if (user) {
      fetchCases();
    }
  }, [user]);

  return {
    cases,
    loading,
    error,
    createCase,
    updateCase,
    deleteCase,
    forceRefresh,
    fetchCases,
  };
};
